import React, { useState, useEffect } from 'react';
import { logAndCaptureException } from 'utils';
import InputMask from 'react-input-mask';

import { ESnapshotExists, EOrganization } from 'lib/types';
import { State } from 'lib/enums';

import { removeUndefinedFields } from 'lib/helpers';
import { ColumnService } from 'lib/services/directory';
import SubmitButton from '../../../components/SubmitButton';
import SelectDropdown from '../../placeScroll/SelectDropdown';
import SettingsHeader from '../SettingsHeader';
import * as validators from '../../register/organization/validators';
import OrganizationSearchSettingToggle from '../OrganizationSearchSettingToggle';

type LawSettingsInfoProps = {
  activeOrganization: ESnapshotExists<EOrganization>;
};

export default function LawSettingsInfo({
  activeOrganization
}: LawSettingsInfoProps) {
  const [name, setName] = useState(activeOrganization.data().name);
  const [address, setAddress] = useState(activeOrganization.data().address);
  const [city, setCity] = useState(activeOrganization.data().city);
  const [state, setState] = useState(activeOrganization.data().state);
  const [zipCode, setZipCode] = useState(activeOrganization.data().zipCode);
  const [addressLine2, setAddressLine2] = useState(
    activeOrganization.data().addressLine2
  );
  const [phone, setPhone] = useState(activeOrganization.data().phone);
  const [error, setError] = useState('');
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [isZipValid, setIsZipValid] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setName(activeOrganization.data().name);
    setAddress(activeOrganization.data().address);
    setCity(activeOrganization.data().city);
    setState(activeOrganization.data().state);
    setZipCode(activeOrganization.data().zipCode);
    setAddressLine2(activeOrganization.data().addressLine2);
    setPhone(activeOrganization.data().phone);
  }, [activeOrganization]);

  useEffect(() => {
    if (phone) {
      setIsPhoneValid(!phone.includes('_'));
    } else setIsPhoneValid(false);
  }, [phone]);

  useEffect(() => {
    const zipRegex = /[0-9]{5}/;
    if (zipCode) {
      setIsZipValid(zipRegex.test(zipCode));
    } else if (zipCode?.length <= 0) {
      setIsZipValid(false);
    } else setIsZipValid(true);
  }, [zipCode]);

  const handleZipCodeInput = (newValue: string) => {
    if (validators.zip(newValue) && newValue.length >= 0) setZipCode(newValue);
  };

  const handlePhoneInput = (newValue: string) => {
    setPhone(newValue);
  };

  const handleSubmit = async () => {
    setError('');
    if (!state) {
      setError('Select state to continue');
      return;
    }

    if (!isPhoneValid) {
      setError('Enter a valid phone number to continue');
      return;
    }
    if (!isZipValid) {
      setError('Enter a valid zip code to continue');
      return;
    }

    setLoading(true);

    try {
      await activeOrganization.ref.update(
        removeUndefinedFields({
          name,
          address,
          city,
          state,
          zipCode,
          addressLine2,
          phone
        })
      );
      setLoading(false);
    } catch (err) {
      logAndCaptureException(
        ColumnService.WEB_PLACEMENT,
        err,
        'Failed to update active organization',
        {
          activeOrgId: activeOrganization.id
        }
      );
      setError(error);
    }
  };

  return (
    <div className="bg-white sm:rounded-lg border border-gray-300 shadow">
      <form
        onSubmit={async e => {
          e.preventDefault();
          await handleSubmit();
        }}
        className="flex flex-col w-full"
      >
        <SettingsHeader
          header="Law Firm Info"
          description="Tell us a bit more about your organization."
        />
        <div className="px-4 py-5 sm:p-6">
          <div className="flex justify-center items-center mt-2">
            <input
              className="appearance-none rounded relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-sm sm:leading-5"
              id="name"
              required
              autoComplete="none"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Name of law firm *"
            />
          </div>
          <div className="flex justify-center mt-4">
            <input
              className="relative rounded block w-6/12 mr-2 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-sm sm:leading-5"
              id="address"
              required
              autoComplete="none"
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="Address Line 1 *"
            />
            <input
              className="relative rounded block w-6/12 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-sm sm:leading-5"
              id="city"
              required
              autoComplete="none"
              value={city}
              onChange={e => setCity(e.target.value)}
              placeholder="City *"
            />
          </div>
          <div className="flex justify-center mt-4">
            <input
              className="relative rounded block w-6/12 mr-2 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-sm sm:leading-5"
              id="address-line-2"
              autoComplete="none"
              value={addressLine2}
              onChange={e => setAddressLine2(e.target.value || '')}
              placeholder="Address Line 2"
            />
            <div className="w-6/12">
              <SelectDropdown
                id="state"
                onChange={(selected: { id: number; data: any }) => {
                  if (selected) {
                    setState(selected.id);
                  }
                }}
                placeholder="State *"
                options={State.items().map((state: Record<string, any>) => ({
                  id: state.value,
                  label: state.label
                }))}
                selected={State.items().filter(item => {
                  return item.value === state
                    ? { id: item.value, label: item.label }
                    : '';
                })}
                value={State.items().filter(item => {
                  return item.value === state
                    ? { id: item.value, label: item.label }
                    : '';
                })}
                borderRadius={4}
                borderColor={'#e2e8f0'}
                placeholderText={'#a0aec0'}
                required
                noShadow
              />
            </div>
          </div>
          <div className="flex justify-center mt-4">
            <input
              className={`relative rounded block w-6/12 mr-2 px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none ${
                isZipValid
                  ? 'focus:shadow-outline-blue focus:border-blue-300'
                  : 'border-red-400'
              } focus:z-10 sm:text-sm sm:leading-5`}
              id="zipCode"
              required
              value={zipCode}
              name="zipCode"
              autoComplete=""
              onChange={e => handleZipCodeInput(e.target.value)}
              placeholder="Zip *"
            />
            <div className="w-6/12">
              <InputMask
                mask="(999) 999-9999"
                className={`appearance-none rounded relative w-full block px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 focus:z-10 sm:text-sm sm:leading-5 
            ${
              isPhoneValid
                ? 'focus:shadow-outline-blue focus:border-blue-300'
                : 'border-red-400'
            }
            `}
                id="phone"
                required
                autoComplete="none"
                value={phone}
                name="phone"
                onChange={e => handlePhoneInput(e.target.value)}
                placeholder="Phone *"
              />
            </div>
          </div>
          <div className="flex justify-center w-full">
            <p className="text-red-500 text-xs w-6/12">
              {!isZipValid && 'Zipcode should be a 5 digit code.'}
            </p>
            <p className="text-red-500 text-xs w-6/12 ml-3">
              {!isPhoneValid && 'Phone number must be 10 digits.'}
            </p>
          </div>
          <div className="text-red-500 text-xs justify-center">{error}</div>
          <OrganizationSearchSettingToggle />
        </div>
        <div className="flex w-full rounded justify-end bg-gray-100 p-5">
          <SubmitButton
            disabled={loading}
            loading={loading}
            text="Save"
            type="submit"
            id="InfoSave"
          />
        </div>
      </form>
    </div>
  );
}
